import * as React from "react";

import {
  ChevronRightIcon,
  ExpandMoreIcon,
} from "@/shared/foundation/icons/mui";

import { type SlackThreadContext } from "./slackContext";
import styles from "./SlackContextDisclosure.module.css";

interface SlackContextDisclosureProps {
  context: SlackThreadContext;
}

const OMISSION_NOTE = /^\(.* omitted\)$/;

/**
 * The Slack chatter a mention carried, folded under a toggle above the question.
 *
 * A Slack-origin thread's user messages open with what was said in the Slack
 * thread since the agent last answered (see `slackContext.ts`). That is context
 * for the model, and a reader following the thread in Roboto usually wants the
 * question, not the transcript it rode in on -- so it is collapsed by default
 * and labelled with how much is behind it. Expanded, each quoted message keeps
 * its `[time] Name: text` line.
 */
export function SlackContextDisclosure({
  context,
}: SlackContextDisclosureProps) {
  const [expanded, setExpanded] = React.useState(false);
  const messageCount = context.lines.filter(
    (line) => !OMISSION_NOTE.test(line),
  ).length;

  return (
    <div className={styles.disclosure}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ExpandMoreIcon className={styles.chevronIcon} />
        ) : (
          <ChevronRightIcon className={styles.chevronIcon} />
        )}
        {`Slack thread context · ${messageCount} ${messageCount === 1 ? "message" : "messages"}`}
      </button>

      {expanded && (
        <ul className={styles.lines}>
          {context.lines.map((line, index) => (
            <li
              key={`${index}-${line}`}
              className={
                OMISSION_NOTE.test(line) ? styles.omission : styles.line
              }
            >
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
